import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { Attachment, ComposerState } from '@/api/models';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/field';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/time';
import { usePostMessage, useUploadAttachment } from '@/api/queries';
import { ComposerBlocked } from './ComposerBlocked';
import { reasonCopy } from './copy/reasons';

/**
 * The composer.
 *
 * One box, one send, and an honest reason whenever it is closed. The reason
 * always comes from the server's `CanPrompt` predicate - never re-derived from
 * the status here, because the two disagree the first time a hold or a
 * subscription state is involved, and a composer that says "ready" while the
 * server says "held" is worse than one that is simply closed.
 *
 * Uploads: the bytes go browser -> API -> the run's in-memory session
 * directory. They are never written to disk and never committed. That is worth
 * saying on screen, because a client attaching a contract or a brand guide is
 * entitled to know where it went.
 *
 * Idempotency: a `client_message_id` is generated once per composed message and
 * kept across retries, so a double-tap on a slow connection returns the
 * original 202 instead of starting a second run on the same project.
 */

const MAX_TEXT = 20_000;
const MAX_ATTACHMENTS = 10;

const schema = z.object({
  text: z
    .string()
    .trim()
    .min(1, 'Describe the change you want.')
    .max(MAX_TEXT, 'That is longer than we can send. Try splitting it into two requests.'),
});

type FormValues = z.infer<typeof schema>;

const newMessageId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `m-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

interface ComposerProps {
  projectId: number;
  composer: ComposerState | null | undefined;
  /** True while a question is parked - the answer goes in the ask card, not here. */
  askPending: boolean;
  supportEmail?: string;
}

export function Composer({ projectId, composer, askPending, supportEmail }: ComposerProps) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const messageIdRef = useRef<string>(newMessageId());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const post = usePostMessage(projectId);
  const upload = useUploadAttachment(projectId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { text: '' } });

  const text = watch('text');
  const canPrompt = composer?.can_prompt === true;
  const attachmentsAllowed = composer?.attachments_allowed !== false;

  const { ref: registerRef, ...textField } = register('text');

  // Grow the box with the request, up to a point. A person describing a change
  // in five sentences should be able to see all five.
  const autosize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(210, Math.max(62, el.scrollHeight))}px`;
  }, []);

  useEffect(autosize, [text, autosize]);

  const onSubmit = handleSubmit((values) => {
    post.mutate(
      {
        text: values.text.trim(),
        attachment_ids: attachments.length ? attachments.map((a) => a.id) : undefined,
        client_message_id: messageIdRef.current,
      },
      {
        onSuccess: () => {
          reset({ text: '' });
          setAttachments([]);
          setUploadError(null);
          messageIdRef.current = newMessageId();
          requestAnimationFrame(autosize);
        },
      },
    );
  });

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploadError(null);
    const room = MAX_ATTACHMENTS - attachments.length;
    const chosen = Array.from(files).slice(0, Math.max(0, room));
    if (chosen.length < files.length) {
      setUploadError(`You can attach up to ${MAX_ATTACHMENTS} files to one request.`);
    }

    for (const file of chosen) {
      try {
        const created = await upload.mutateAsync({ file });
        setAttachments((current) => [...current, created]);
      } catch (err) {
        setUploadError(uploadErrorMessage(err, file.name));
        break;
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (!canPrompt) {
    // A parked question is not a closed composer in the client's mind - the
    // answer box is right there in the thread - so say that, in its own words.
    if (askPending && composer?.reason_code === 'awaiting_answer') {
      const copy = reasonCopy('awaiting_answer');
      return (
        <div className="flex items-center gap-3.5 rounded-lg bg-blush px-[22px] py-4" role="status">
          <span aria-hidden className="h-[7px] w-[7px] shrink-0 animate-ledger-pulse rounded-full bg-signal" />
          <div className="min-w-0">
            <p className="text-[0.96875rem] font-semibold tracking-[-0.015em] text-signal">{copy.title}</p>
            <p className="mt-0.5 text-[0.90625rem] text-graphite-500">{copy.body}</p>
          </div>
        </div>
      );
    }
    return <ComposerBlocked composer={composer} supportEmail={supportEmail} />;
  }

  const postError = post.error instanceof ApiError ? post.error : null;
  const blockedByServer = postError?.httpStatus === 409;

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      {blockedByServer ? (
        // The server refused between the composer rendering and the send. Show
        // the reason it gave, not a generic failure - it is the same vocabulary
        // the greyed composer uses, so the person sees one consistent story.
        <ComposerBlocked composer={postError?.composer ?? composer} supportEmail={supportEmail} />
      ) : null}

      {/* The composer is one of the two places a client screen spends its
          accent, and under Soft Pastel it spends it by NOT being white: the well
          is a panel inset in the white stage, and the ring appears only on
          focus. The other place is the parked question — and the two can never
          be present at once, because an unanswered ask returns above this line. */}
      <div
        className={cn(
          'rounded-xl bg-bone p-1.5 transition-shadow',
          'focus-within:shadow-[inset_0_0_0_1.5px_var(--rule-strong)]',
        )}
      >
        <label htmlFor="composer-text" className="sr-only">
          Describe the change you want
        </label>
        <Textarea
          id="composer-text"
          {...textField}
          ref={(el) => {
            registerRef(el);
            textareaRef.current = el;
          }}
          rows={2}
          disabled={post.isPending}
          placeholder="Describe the change you want. Plain English is fine — say what you want different and where."
          className="min-h-[62px] rounded-none bg-transparent px-[18px] pb-1.5 pt-4 text-base focus:shadow-none"
          maxLength={MAX_TEXT}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            // Mid-composition Enter belongs to the IME, which is choosing a
            // candidate, not finishing a sentence. Sending here loses the
            // half-typed word and the request.
            if (event.nativeEvent.isComposing) return;
            // Shift-Enter is the newline. Enter sends, because this is a chat
            // box and every chat box in the world sends on Enter — and the
            // modifier combination is kept working for the muscle memory of
            // anyone who learned it here.
            if (event.shiftKey) return;
            event.preventDefault();
            void onSubmit();
          }}
        />

        {attachments.length ? (
          <ul className="flex flex-wrap gap-2 px-[18px] pb-2.5 pt-1">
            {attachments.map((a) => (
              <li
                key={a.id}
                className="inline-flex items-center gap-2.5 whitespace-nowrap rounded-full bg-bone-raised py-1.5 pl-3.5 pr-2 font-mono text-[0.6875rem] text-graphite-500"
              >
                <span className="max-w-[13rem] truncate">{a.filename}</span>
                <span className="text-graphite-300">{formatBytes(a.size_bytes)}</span>
                <button
                  type="button"
                  aria-label={`Remove ${a.filename}`}
                  className="grid h-5 w-5 place-items-center rounded-full text-graphite-300 hover:bg-bone hover:text-alarm"
                  onClick={() => setAttachments((current) => current.filter((x) => x.id !== a.id))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex flex-wrap items-center gap-2.5 py-1.5 pl-3 pr-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="sr-only"
            id="composer-files"
            onChange={(event) => void onFiles(event.target.files)}
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={!attachmentsAllowed || upload.isPending || attachments.length >= MAX_ATTACHMENTS}
            loading={upload.isPending}
            onClick={() => fileInputRef.current?.click()}
            title={
              attachmentsAllowed
                ? 'Attach a file for this request. Files are passed straight into the work and never stored.'
                : 'Files cannot be attached to this request.'
            }
          >
            <svg aria-hidden viewBox="0 0 14 14" className="h-3.5 w-3.5 stroke-current" fill="none" strokeWidth="1.4">
              <path d="M9.5 4 5 8.5a1.8 1.8 0 0 0 2.5 2.5l4.2-4.2a3.2 3.2 0 0 0-4.5-4.5L3 6.5a4.6 4.6 0 0 0 6.5 6.5" />
            </svg>
            Attach
          </Button>

          <span className="hidden text-[0.78125rem] text-graphite-300 sm:inline">
            {attachments.length
              ? `${attachments.length} of ${MAX_ATTACHMENTS} files · never stored`
              : 'Files go straight into the work and are never stored.'}
          </span>

          <span className="ml-auto flex items-center gap-3.5">
            {text.length > MAX_TEXT * 0.8 ? (
              <span
                className={cn(
                  'font-mono text-[0.6875rem] tabular-nums',
                  MAX_TEXT - text.length < 200 ? 'text-alarm' : 'text-graphite-300',
                )}
              >
                {(MAX_TEXT - text.length).toLocaleString()} characters left
              </span>
            ) : null}
            <Button type="submit" variant="primary" size="sm" loading={post.isPending} disabled={!text.trim()}>
              {post.isPending ? 'Sending' : 'Send'}
            </Button>
          </span>
        </div>
      </div>

      {errors.text ? <p className="text-sm text-alarm">{errors.text.message}</p> : null}
      {uploadError ? <p className="text-sm text-alarm">{uploadError}</p> : null}
      {postError && !blockedByServer ? <p className="text-sm text-alarm">{postError.message}</p> : null}
    </form>
  );
}

function uploadErrorMessage(err: unknown, filename: string): string {
  if (!(err instanceof ApiError)) return `${filename} could not be attached. Try again.`;
  if (err.httpStatus === 413) {
    const limit = (err.detail as { limit_bytes?: number } | undefined)?.limit_bytes;
    return limit
      ? `${filename} is too large. The limit is ${formatBytes(limit)}.`
      : `${filename} is too large to attach.`;
  }
  if (err.httpStatus === 415) return `${filename} is not a file type we can accept.`;
  if (err.httpStatus === 409) return reasonCopy(err.reasonCode).title;
  return `${filename} could not be attached. ${err.message}`;
}
